import type { UploadedVideo } from '../utils/uploadHelpers'
import { totalSeconds } from '../utils/referenceLimits'
import Tooltip from '../../../components/Tooltip'
import SideTooltip from '../../../components/SideTooltip'
import { Icon } from '../../../../public/icons/Icon.tsx'

export interface MultiVideoRowProps {
	videos: UploadedVideo[]
	maxVideos: number
	/** Суммарный потолок роликов у модели, с (Seedance 2.5 — 30, 2.0 — 15). */
	maxTotalSeconds?: number
	/** Форматы для окна выбора файла по модели; без него — общий список компонента. */
	accept?: string
	isLoading: boolean
	isUploading: boolean
	inputRef: React.RefObject<HTMLInputElement | null>
	onUpload: (e: React.ChangeEvent<HTMLInputElement>) => void
	onRemove: (idx: number) => void
	tooltip?: string
	/** Пикер источника («Загрузить / Из моих генераций») у кнопки добавления. */
	onShowPicker?: (buttonEl: HTMLElement) => void
	/** Скелет-плитки пустых слотов до лимита (tool-режим: юзер видит,
	 *  сколько файлов можно добавить). В классическом баре не рендерятся. */
	slotSkeletons?: boolean
	/** Подпись-блокер под рядом (например, «Переделать видео» с несколькими роликами). */
	note?: string
}

export default function MultiVideoRow({
	videos,
	maxVideos,
	maxTotalSeconds,
	accept,
	isLoading,
	inputRef,
	onUpload,
	onRemove,
	tooltip,
	onShowPicker,
	slotSkeletons,
	note,
}: MultiVideoRowProps) {
	const totalDuration = totalSeconds(videos)
	// Без лимита у модели предупреждения нет: число, которого мы не знаем, не показываем.
	const overLimit = maxTotalSeconds != null && totalDuration > maxTotalSeconds

	const addBtn = (
		<button
			type="button"
			className="gm-upload-btn gm-upload-btn--sm"
			onClick={(e) => {
				if (onShowPicker) onShowPicker(e.currentTarget)
				else inputRef.current?.click()
			}}
			disabled={isLoading}
			aria-label="Добавить видео"
		>
			<Icon id="gen-video" size={20} />
		</button>
	)
	return (
		<>
			<div className="gm-multi-row">
				{/* Обёртка тумб: в баре display:contents, в tool-режиме — зона со скроллом. */}
				<div className="gm-multi-row__items">
				{videos.map((v, idx) => {
					const card = (
						<div className="gm-upload-thumb gm-upload-thumb--sm">
							<video
								src={v.preview}
								muted
								playsInline
								preload="metadata"
								className="gm-upload-thumb__media"
								onLoadedData={(e) => {
									(e.target as HTMLVideoElement).currentTime = 0.1
								}}
							/>
							{!v.url && <div className="gm-upload-thumb__overlay">…</div>}
							{v.url && (
								<div className="gm-upload-thumb__ok">
									<Icon id="gen-check" size={10} />
								</div>
							)}
							<button
								className="gm-upload-thumb__remove"
								onClick={() => onRemove(idx)}
								aria-label="Удалить"
							>
								<Icon id="gen-close" size={14} />
							</button>
						</div>
					)
					return (
						<Tooltip key={v.preview} videoPreview={v.preview}>
							{card}
						</Tooltip>
					)
				})}
				{slotSkeletons &&
					Array.from({ length: Math.max(0, maxVideos - videos.length) }).map((_, i) => (
						<span key={`skel-${i}`} className="gm-upload-skel" aria-hidden="true" />
					))}
				</div>
				{videos.length < maxVideos &&
					(tooltip ? <SideTooltip text={tooltip}>{addBtn}</SideTooltip> : addBtn)}
				<input
					ref={inputRef}
					type="file"
					accept={accept || "video/mp4,video/quicktime,video/webm"}
					multiple
					onChange={onUpload}
					// isUploading НЕ блокирует: параллельные загрузки легитимны (см. MultiImgRow)
					disabled={isLoading}
					style={{ display: 'none' }}
				/>
			</div>
			{overLimit && (
				<div
					className="gm-multi-row__warning"
					style={{ fontSize: '0.75rem', color: '#f59e0b', marginTop: 4 }}
				>
					⚠ Суммарная длительность видео: {Math.round(totalDuration)} сек (максимум {maxTotalSeconds} сек) — уберите ролик или загрузите короче
				</div>
			)}
			{note && (
				<div className="gm-multi-row__warning" style={{ fontSize: '0.75rem', color: '#f59e0b', marginTop: 4 }}>
					⚠ {note}
				</div>
			)}
		</>
	)
}
